import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import useEquipmentInfo from "../../hooks/useEquipmentInfo";
import EquipmentInfo from "../../pages/infos/equipment/EquipmentInfo";
import { useLocalizedPath } from "../../hooks/useLocale";
import LanguageSelector from "../LanguageSelector";

type GeneratedFeatureProps = {
  /** When rendered from LocaleLayout for a QR code path, param is named `locale` */
  code?: string;
};

const GeneratedFeature = ({ code }: GeneratedFeatureProps) => {
  const params = useParams();
  const objCode = code ?? params.objCode ?? params.locale;
  const localizedPath = useLocalizedPath();
  const intl = useIntl();
  const { isLoading, equipmentInfo, isNotFound, error, getEquipmentInfo } = useEquipmentInfo();

  useEffect(() => {
    if (objCode) {
      getEquipmentInfo(objCode);
    }
  }, [getEquipmentInfo, objCode]);

  if (!objCode) {
    return <Navigate to={localizedPath('/')} />;
  }

  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-blue-gradient grid place-items-center">
        <p className="text-xl text-white">{intl.formatMessage({ id: 'common.loading' })}</p>
      </div>
    );
  }

  if (error && !isNotFound) {
    return (
      <div className="w-dvw h-dvh grid place-items-center relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="flex flex-col items-center">
          <h1 className="mb-3 text-4xl text-shadow">
            {intl.formatMessage({ id: 'common.serviceUnavailable' })}
          </h1>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (isNotFound) {
    return <Navigate to={localizedPath('/')} />;
  }

  if (equipmentInfo) {
    return (
      <div className="relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <EquipmentInfo data={equipmentInfo} />
      </div>
    );
  }

  return null;
};

export default GeneratedFeature;
